import { IVipRoyaltyPointsPreview } from '../models/vip/i-vip-royalty-points.interface';

import { RoyaltyMockState } from '../utils/dev-royalty-mocks.utils';

/**
 * Canned data for the royalty → VIP Points perk, one entry per state a local dev needs to look at.
 *
 * WHY THIS EXISTS. The perk is fed by two external providers nobody on this side can put numbers
 * into: the royalty data handler (behind `GET /vip/royalty-points/preview/`) and the passive-income
 * insight proxy (the claimable balance). Locally both answer with nothing, so all three surfaces —
 * the VIP page panel, the profile card's note and the claim modal's estimate — render empty. That
 * is ALSO exactly what they correctly render for a holder the perk does not pay, so "broken" and
 * "working, not eligible" are indistinguishable without a mock.
 *
 * FIDELITY. Every payload below is what `royalty/views.py` would actually serialise for that case,
 * not a plausible-looking invention:
 *   - `royalty_rmv` and `multiplier` are STRINGS. `_rmv()` renders the Decimal with `normalize():f`
 *     and `multiplier` is `str()` of a `DecimalField(decimal_places=2)`, which is why the rate reads
 *     "2.50" and not "2.5". A JS number would round both at the eighteenth place.
 *   - `eligible` is the backend's own composite — `vip_level is not None AND multiplier > 0` — so
 *     `non_vip` and `vip_zero` differ in `vip_level` while agreeing that nothing converts.
 *   - `non_vip` carries a ZERO `royalty_rmv`. The preview used to run a non-VIP holder through the
 *     provider so a teaser could quote a real figure; that was removed, and `compute_royalty_points`
 *     now short-circuits at `NON_VIP` before the provider call, leaving the dataclass defaults.
 *     A mock showing a non-VIP a real royalty figure would be inventing a payload the API stopped
 *     producing.
 *
 * The two failure states are deliberately absent from the payload map: a 404 and a 503 have no
 * body worth rendering, and the hook distinguishes them by STATUS. `ROYALTY_FAILURE_STATUS` below
 * is their whole definition. The types enforce that split — a payload cannot be written for a
 * failure state, and a failure state cannot acquire one.
 */

/** The states that answer with a body. */
type RoyaltyMockPayloadState = Exclude<RoyaltyMockState, 'unavailable' | 'feature_off'>;

/** The states that answer with an HTTP failure instead of a body. */
type RoyaltyMockFailureState = Extract<RoyaltyMockState, 'unavailable' | 'feature_off'>;

/** The perk's launch date, as the API sends it: an ISO calendar date, or `null` when unset. */
const SINCE_MOCK = '2026-03-01';

/**
 * THE CLAIMABLE BALANCE, from the OTHER provider — the profile card's "Available to Claim".
 *
 * Deliberately much larger than, and not a round multiple of, the converting figure below. The
 * gap is the whole reason `constants/royalty-points-copy.constant` exists: this number is every
 * undrawn royalty a wallet has, while the preview counts only World NFT royalty, only since
 * launch, and only what a previous claim has not already converted. A mock where the two matched
 * would hide the one thing on these screens most worth looking at, and would quietly "prove" a
 * copy layer correct that had never been read against a discrepancy.
 */
export const ROYALTY_CLAIMABLE_RMV_MOCK = 9481.62;

/**
 * THE ON-CHAIN HALF of the claimable balance, in wei, as `CashierService.getWithdrawn` returns it.
 *
 * `useSelectedWalletRoyalties` computes `availableToCollect` as `max(0, floor(total - withdrawn))`
 * from ONE `Promise.all` over the passive-income proxy and this contract read. Locally the
 * contract read rejects, and a rejection in that `Promise.all` takes the mocked total down with
 * it — the catch zeroes both, the Claim button never enables, and the claim modal (one of only
 * two places the points figure renders at all) cannot be opened. So the mock has to answer for
 * this half too.
 *
 * Zero is the honest answer for a fabricated wallet that has never claimed, and it is the only
 * value that cannot eat into the gap this mock exists to show: the whole mocked total stays
 * claimable, so the claimable figure stays visibly larger than the converting one in every state.
 */
export const ROYALTY_WITHDRAWN_WEI_MOCK = '0';

/** The converting figure. Shared by the states that have royalty accrued, so the gap stays one number. */
const ROYALTY_CONVERTING_RMV_MOCK = '1284.375';

/** A mid-ladder rate, rendered by `formatPerkValue` as "x2.50". */
const MULTIPLIER_MOCK = '2.50';

/** `1284.375 × 2.50 = 3210.9375`, floored server-side — the API never sends a fractional point. */
const POINTS_MOCK = 3210;

export const ROYALTY_PREVIEW_MOCKS: Record<RoyaltyMockPayloadState, IVipRoyaltyPointsPreview> = {
  eligible: {
    royalty_rmv: ROYALTY_CONVERTING_RMV_MOCK,
    points: POINTS_MOCK,
    vip_level: 7,
    multiplier: MULTIPLIER_MOCK,
    since: SINCE_MOCK,
    eligible: true,
  },
  empty: {
    // The provider answered and the answer is nothing. Same level and rate as `eligible` — the
    // difference between the two states is the royalty, not the holder.
    royalty_rmv: '0',
    points: 0,
    vip_level: 7,
    multiplier: MULTIPLIER_MOCK,
    since: SINCE_MOCK,
    eligible: true,
  },
  non_vip: {
    // `compute_royalty_points` stops at NON_VIP before the provider call, so these are the
    // `RoyaltyPoints` dataclass defaults: `Decimal(0)` rendered by `str()`, hence "0" not "0.00".
    royalty_rmv: '0',
    points: 0,
    vip_level: null,
    multiplier: '0',
    since: SINCE_MOCK,
    eligible: false,
  },
  vip_zero: {
    // A real level with a real config row, so this one DOES reach the provider: royalty accrues,
    // it just converts at nothing. `multiplier` comes from a DecimalField(decimal_places=2), which
    // is why a zero rate here reads "0.00" while the non-VIP default above reads "0".
    royalty_rmv: ROYALTY_CONVERTING_RMV_MOCK,
    points: 0,
    vip_level: 0,
    multiplier: '0.00',
    since: SINCE_MOCK,
    eligible: false,
  },
};

/** The status each failure state reproduces. The hook reads 404 and everything-else differently. */
export const ROYALTY_FAILURE_STATUS: Record<RoyaltyMockFailureState, number> = {
  feature_off: 404,
  unavailable: 503,
};

/** The wording the endpoint really sends, so a network panel reads the same as production. */
export const ROYALTY_FAILURE_DETAIL: Record<RoyaltyMockFailureState, string> = {
  feature_off: 'Royalty points conversion is not enabled.',
  unavailable: 'The royalty points estimate is unavailable right now.',
};

export const isRoyaltyMockFailureState = (state: RoyaltyMockState): state is RoyaltyMockFailureState =>
  state === 'feature_off' || state === 'unavailable';
